"use client";

import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";

import { ArticleContent } from "./Article/ArticleContent";
import { LastBoaSementeBlogPostContent } from "./LastBoaSementeBlogPostContent";
import { PublisherProducts } from "./PublisherProducts";
import type { GetArticleByIdQuery } from "../gql/graphql";
import type { PublisherData } from "../types/Publisher";
import { auth } from "../utils/auth/initFirebase";

export default function ProtectedArticle({ id, publisherData }: { id: string; publisherData: PublisherData | null }) {
  const { isLoading, error, data } = useQuery<NonNullable<GetArticleByIdQuery["articles"]> | null>({
    queryKey: ["protectedArticle", id],
    queryFn: async (): Promise<NonNullable<GetArticleByIdQuery["articles"]> | null> => {
      // The server checks the login and the subscription before returning the content
      const idToken = await auth.currentUser?.getIdToken();
      const response = await fetch(`/api/artigos-protegidos/${encodeURIComponent(id)}/`, {
        headers: { authorization: `Bearer ${idToken}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to load protected article: ${response.status}`);
      }

      return response.json();
    },
  });

  if (isLoading) {
    return (
      <Box display="flex" minHeight="100%" flexWrap="wrap" alignContent="center" justifyContent="center">
        <Typography style={{ flexBasis: "100%" }} variant="h4" align="center">
          Carregando conteúdo protegido
        </Typography>
        <Box marginTop={2} width="50%" component={LinearProgress} />
      </Box>
    );
  }

  if (error || !data) {
    return <div>oops :(</div>;
  }

  if (!data.title || !data.teste || !data.issue) {
    return <div>Dados incompletos</div>;
  }

  return (
    <ArticleContent
      article={data}
      sidebar={
        <Box display="flex" flexDirection="column" gap={4} sx={{ position: "sticky", top: "1rem" }}>
          {publisherData?.blog ? (
            <LastBoaSementeBlogPostContent blog={publisherData.blog} postTagline={publisherData.postTagline} />
          ) : null}
          <PublisherProducts
            products={publisherData?.products || []}
            productsTagline={publisherData?.productsTagline ?? null}
          />
        </Box>
      }
    />
  );
}
